import { useState, useEffect, useRef } from 'react';
import DailyIframe from '@daily-co/daily-js';
import { useAuth } from './useAuth';
import { supabase } from '../lib/supabase';
import { showToast } from '../utils/toast';

interface DailyParticipant {
  id: string;
  name?: string;
  isLocal: boolean;
  audioTrack?: MediaStreamTrack;
  videoTrack?: MediaStreamTrack;
  screenVideoTrack?: MediaStreamTrack;
  screenAudioTrack?: MediaStreamTrack;
  isCameraOn: boolean;
  isMicrophoneOn: boolean;
  isScreenshareOn: boolean;
}

interface DailyMessage {
  id: string;
  sender: {
    id: string;
    name: string;
  };
  text: string;
  timestamp: number;
}

export function useDailyRoom() {
  const [callFrame, setCallFrame] = useState<any>(null);
  const [roomUrl, setRoomUrl] = useState<string | null>(null);
  const [roomName, setRoomName] = useState<string | null>(null);
  const [participants, setParticipants] = useState<DailyParticipant[]>([]);
  const [chatMessages, setChatMessages] = useState<DailyMessage[]>([]);
  const [isJoined, setIsJoined] = useState(false);
  const [isMicOn, setIsMicOn] = useState(false);
  const [isCameraOn, setIsCameraOn] = useState(false);
  const [isScreensharing, setIsScreensharing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();

  // Cleanup function for the Daily call
  const cleanupCall = () => {
    if (callFrame) {
      callFrame.destroy();
      setCallFrame(null);
      setIsJoined(false);
      setParticipants([]);
      setChatMessages([]);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupCall();
    };
  }, []);

  // Create a new Daily room
  const createRoom = async (sessionId: string, sessionTitle: string) => {
    if (!user) {
      showToast.auth.authError('Please sign in to create a room');
      throw new Error('Not authenticated');
    }

    setLoading(true);
    setError(null);

    try {
      // Call the Supabase Edge Function to create a Daily room
      const { data, error } = await supabase.functions.invoke('create-daily-room', {
        body: {
          roomName: `session-${sessionId}`,
          expiryMinutes: 120, // 2 hours
          enableRecording: true
        }
      });

      if (error) throw new Error(error.message);
      if (!data || !data.url) throw new Error('Failed to create room');

      // Update the expert session with the room URL
      const { error: updateError } = await supabase
        .from('expert_sessions')
        .update({
          daily_room_name: data.name,
          daily_room_url: data.url,
          status: 'live',
          updated_at: new Date().toISOString()
        })
        .eq('id', sessionId);

      if (updateError) throw updateError;

      setRoomUrl(data.url);
      setRoomName(data.name);
      
      return data.url;
    } catch (error: any) {
      console.error('Error creating Daily room:', error);
      setError(error.message || 'Failed to create room');
      showToast.system.error('Failed to create live session room');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Join an existing Daily room
  const joinRoom = async (roomUrl: string, userName: string, container: HTMLDivElement) => {
    if (!roomUrl) {
      setError('Room URL is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Clean up any existing call
      cleanupCall();

      // Create a new Daily call frame
      const frame = DailyIframe.createFrame(container, {
        iframeStyle: {
          width: '100%',
          height: '100%',
          border: 'none',
          backgroundColor: 'transparent'
        },
        showLeaveButton: true,
        showFullscreenButton: true
      });

      // Set up event listeners
      frame
        .on('joined-meeting', (event: any) => {
          setIsJoined(true);
          setLoading(false);
          
          // Update participant state
          const participantsObj = frame.participants();
          const participantList = Object.values(participantsObj) as DailyParticipant[];
          setParticipants(participantList);
          
          // Check initial device states
          const localParticipant = participantList.find(p => p.isLocal);
          if (localParticipant) {
            setIsMicOn(localParticipant.isMicrophoneOn);
            setIsCameraOn(localParticipant.isCameraOn);
          }
          
          showToast.system.success('Joined live session successfully');
        })
        .on('left-meeting', () => {
          setIsJoined(false);
          cleanupCall();
          showToast.system.info('Left the live session');
        })
        .on('participant-joined', (event: any) => {
          // Update participants when someone joins
          const participantsObj = frame.participants();
          const participantList = Object.values(participantsObj) as DailyParticipant[];
          setParticipants(participantList);
        })
        .on('participant-left', (event: any) => {
          // Update participants when someone leaves
          const participantsObj = frame.participants();
          const participantList = Object.values(participantsObj) as DailyParticipant[];
          setParticipants(participantList);
        })
        .on('camera-error', (event: any) => {
          console.error('Camera error:', event);
          showToast.system.error('Camera error: ' + event.errorMsg);
        })
        .on('microphone-error', (event: any) => {
          console.error('Microphone error:', event);
          showToast.system.error('Microphone error: ' + event.errorMsg);
        })
        .on('error', (event: any) => {
          console.error('Daily error:', event);
          setError(event.errorMsg || 'An error occurred');
          showToast.system.error('Session error: ' + (event.errorMsg || 'Unknown error'));
        })
        .on('app-message', (event: any) => {
          // Handle chat messages
          if (event.data && event.data.type === 'chat-message') {
            const message: DailyMessage = {
              id: `msg-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
              sender: {
                id: event.fromId,
                name: event.data.senderName || 'Unknown'
              },
              text: event.data.text,
              timestamp: Date.now()
            };
            
            setChatMessages(prev => [...prev, message]);
          }
        });

      // Join the meeting
      await frame.join({
        url: roomUrl,
        userName: userName
      });

      setCallFrame(frame);
      setRoomUrl(roomUrl);
    } catch (error: any) {
      console.error('Error joining Daily room:', error);
      setError(error.message || 'Failed to join room');
      setLoading(false);
      showToast.system.error('Failed to join live session');
    }
  };

  // Leave the current room
  const leaveRoom = () => {
    if (callFrame && isJoined) {
      callFrame.leave();
    }
    cleanupCall();
  };

  // Toggle microphone
  const toggleMic = () => {
    if (callFrame && isJoined) {
      callFrame.setLocalAudio(!isMicOn);
      setIsMicOn(!isMicOn);
    }
  };

  // Toggle camera
  const toggleCamera = () => {
    if (callFrame && isJoined) {
      callFrame.setLocalVideo(!isCameraOn);
      setIsCameraOn(!isCameraOn);
    }
  };

  // Toggle screen sharing
  const toggleScreenshare = async () => {
    if (!callFrame || !isJoined) return;

    try {
      if (isScreensharing) {
        await callFrame.stopScreenShare();
      } else {
        await callFrame.startScreenShare();
      }
      setIsScreensharing(!isScreensharing);
    } catch (error) {
      console.error('Error toggling screenshare:', error);
      showToast.system.error('Failed to toggle screen sharing');
    }
  };

  // Send a chat message
  const sendChatMessage = (text: string) => {
    if (!callFrame || !isJoined || !text.trim()) return;

    try {
      callFrame.sendAppMessage({
        type: 'chat-message',
        text: text.trim(),
        senderName: user?.user_metadata?.full_name || 'You'
      }, '*'); // Send to all participants

      // Add message to local state
      const message: DailyMessage = {
        id: `msg-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
        sender: {
          id: 'local',
          name: user?.user_metadata?.full_name || 'You'
        },
        text: text.trim(),
        timestamp: Date.now()
      };
      
      setChatMessages(prev => [...prev, message]);
    } catch (error) {
      console.error('Error sending chat message:', error);
      showToast.system.error('Failed to send message');
    }
  };

  return {
    containerRef,
    roomUrl,
    roomName,
    participants,
    chatMessages,
    isJoined,
    isMicOn,
    isCameraOn,
    isScreensharing,
    error,
    loading,
    createRoom,
    joinRoom,
    leaveRoom,
    toggleMic,
    toggleCamera,
    toggleScreenshare,
    sendChatMessage
  };
}